import React, { MouseEvent, useEffect, useRef, useState } from "react";
import {
  IParsedIndicatorReport,
  IReportDealerCollection,
} from "~/pages/IndicatorReports/ViewReport";
import { Indicator, RegMenInfo } from "~/schemas/generated/graphql";
import s from "./s.less";
import { useIntersectionObserver } from "~/hooks/useIntersectionObserver";
import clsx from "clsx";
import Preloader from "~/components/Preloader";

interface ITableGen {
  parsedReport: IParsedIndicatorReport;
  paginationTrigger: () => void;
  paginationLoading: boolean;
  metaDataValueDecorator?: (key: string, value: any) => any;
}

const TableGen = ({
  parsedReport,
  paginationTrigger,
  paginationLoading,
  metaDataValueDecorator,
}: ITableGen) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const intersectionObserverItemRef = useRef<HTMLTableRowElement>(null);

  const isInView = useIntersectionObserver({
    target: intersectionObserverItemRef,
  });

  const [startCoords, setStartCoords] = useState({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const onMouseDownHandler = (e: MouseEvent<HTMLTableSectionElement>) => {
    setMouseIsDown(true);
    const startX = e.pageX - containerRef.current.offsetLeft;
    const startY = e.pageY - containerRef.current.offsetTop;

    setStartCoords({
      startX,
      startY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    });
  };
  const mouseIsUpHandler = () => setMouseIsDown(false);

  const onMouseMoveHandler = (e: MouseEvent<HTMLTableSectionElement>) => {
    if (mouseIsDown) {
      const x = e.pageX - containerRef.current.offsetLeft;
      const y = e.pageY - containerRef.current.offsetTop;
      containerRef.current.scrollLeft =
        startCoords.scrollLeft - (x - startCoords.startX);
      containerRef.current.scrollTop =
        startCoords.scrollTop - (y - startCoords.startY);
    }
  };

  const getValueFromIndicator = (
    { dValues }: IReportDealerCollection,
    indicator: Indicator
  ) => {
    const value = dValues.find((value) => value.IndicatorId === indicator.id);

    if (!!value?.stringValue) return value.stringValue;
    if (!!value?.doubleValue) return value.doubleValue;
    return "";
  };

  const getValueFromRegMen = (
    { dRegMans }: IReportDealerCollection,
    RegManInfo: RegMenInfo
  ) => {
    return dRegMans && !!dRegMans[RegManInfo.role]
      ? dRegMans[RegManInfo.role].fullname
      : "";
  };

  useEffect(() => {
    if (isInView) paginationTrigger();
  }, [isInView]);

  return (
    <>
      <div
        className={clsx(s.container, {
          [s.loading]: paginationLoading,
        })}
        ref={containerRef}
        onMouseLeave={mouseIsUpHandler}
        onMouseUp={mouseIsUpHandler}
      >
        <table className={s.table}>
          <thead>
            <tr>
              <th></th>
              {!!parsedReport &&
                Object.keys(parsedReport.metadataCollection).map(
                  (metaDataKey, idx) => (
                    <th key={metaDataKey + idx}>
                      <div className={s.tdContainer}>
                        {parsedReport.metadataCollection[metaDataKey]}
                      </div>
                    </th>
                  )
                )}
              {!!parsedReport &&
                parsedReport.regMenCollection.map((regMen, idx) => (
                  <th key={regMen.role + idx}>
                    <div className={s.tdContainer}>{regMen.roleRu}</div>
                  </th>
                ))}
              {!!parsedReport &&
                parsedReport.indicatorsCollection.map((indicator, idx) => (
                  <th key={indicator.id}>
                    <div className={s.tdContainer}>{indicator.nameRu}</div>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody
            onMouseDown={onMouseDownHandler}
            onMouseUp={mouseIsUpHandler}
            onMouseMove={onMouseMoveHandler}
          >
            {!!parsedReport &&
              parsedReport.dealersCollection.map((dealer, idx) => (
                <tr key={dealer.dName + idx}>
                  <td>
                    <div className={s.tdContainer}>{dealer.dName}</div>
                  </td>

                  {!!parsedReport &&
                    Object.keys(parsedReport.metadataCollection).map(
                      (metaDataKey, idx) => (
                        <td key={metaDataKey + idx}>
                          <div className={s.tdContainer}>
                            {metaDataValueDecorator
                              ? metaDataValueDecorator(
                                  metaDataKey,
                                  dealer.dMeta[metaDataKey]
                                )
                              : dealer.dMeta[metaDataKey]}
                          </div>
                        </td>
                      )
                    )}

                  {parsedReport.regMenCollection.map((regMen, idx) => (
                    <td key={regMen.role + idx}>
                      <div className={s.tdContainer}>
                        {getValueFromRegMen(dealer, regMen)}
                      </div>
                    </td>
                  ))}

                  {parsedReport.indicatorsCollection.map((indicator) => (
                    <td key={indicator.id}>
                      <div className={s.tdContainer}>
                        {getValueFromIndicator(dealer, indicator)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            <tr style={{ width: "100%" }} ref={intersectionObserverItemRef} />
          </tbody>
        </table>
      </div>
      {paginationLoading && (
        <div className={s.preloaderWrapper}>
          <Preloader />
        </div>
      )}
    </>
  );
};

export default TableGen;
